import { useEffect, useState } from 'react';
import { Select, Tag, message } from 'antd';
import { SwapOutlined } from '@ant-design/icons';
import { getToken } from '@/utils/auth';

const API = 'https://api.chpen.com/api/auth/v1/';

type Item = {
  company_id: number;
  company_name: string | null;
  role_names: string[];
  is_super: boolean;
  is_primary: number;
};

/**
 * 780CY-54K-55u · 企业身份切换器（权限纪律：创始人带头执行）
 *
 * 口径：权限 = f(账号, 当前企业上下文)
 *   · 切到上海企庭实业股份有限公司 = 超级管理员
 *   · 切到其他企业               = 管理人权限（对公钱包只管本企业）
 *   · 切到自然人                 = 个人身份，不得触碰对公钱包
 * 切换后必须整体重取权限与菜单 —— 用 reload 保证「看到的」和「后端判的」一致
 */
export default function CompanySwitcher() {
  const [list, setList] = useState<Item[]>([]);
  const [current, setCurrent] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = getToken();
        if (!token) return;
        const r = await fetch(API + 'company-list', {
          headers: { Accept: 'application/json', Authorization: 'Bearer ' + token },
        });
        const j = await r.json();
        if (!alive) return;
        if (j && j.code === 0 && Array.isArray(j?.data?.list)) {
          setList(j.data.list);
          setCurrent(Number(j.data.current ?? 0));
        }
      } catch {
        /* 拿不到清单就不显示切换器，绝不阻断后台使用 */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const companies = list.filter((x) => x.company_id > 0);
  // 可切换企业 <=1 家 -> 不展示（绝大多数管理员只有 1 家，避免干扰）
  if (companies.length <= 1) return null;

  const cur = list.find((x) => x.company_id === current);

  const onChange = async (v: number) => {
    if (v === current) return;
    setLoading(true);
    try {
      const token = getToken();
      const r = await fetch(API + 'switch-company', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + (token || ''),
        },
        body: JSON.stringify({ company_id: v }),
      });
      const j = await r.json();
      if (j && j.code === 0) {
        const name = j?.data?.company_name || (v === 0 ? '自然人（个人身份）' : '已切换');
        message.success('已切换身份：' + name);
        setTimeout(() => window.location.reload(), 300);
        return;
      }
      message.error(j?.msg || '切换失败：无权切换到该身份');
    } catch {
      message.error('切换失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: 10 }}>
      <Select<number>
        size="small"
        style={{ minWidth: 200, fontSize: 13 }}
        value={current ?? 0}
        onChange={onChange}
        loading={loading}
        suffixIcon={<SwapOutlined />}
        options={list.map((x) => ({
          value: x.company_id,
          label:
            x.company_id === 0
              ? '自然人（个人身份）'
              : x.company_name || '企业#' + x.company_id,
        }))}
      />
      <span style={{ marginLeft: 6 }}>
        {!cur || cur.company_id === 0 ? (
          <Tag color="default">自然人</Tag>
        ) : cur.is_super ? (
          <Tag color="red">超级管理员</Tag>
        ) : (
          <Tag color="blue">管理人</Tag>
        )}
      </span>
    </span>
  );
}
